import React, { useEffect, useState } from 'react';
import { db, storage, auth } from './firebase';
import {
  collection,
  query,
  where,
  deleteDoc,
  doc,
  onSnapshot,
  updateDoc
} from 'firebase/firestore';
import { getDownloadURL, ref, deleteObject } from 'firebase/storage';
import { FaExclamationTriangle, FaShieldAlt, FaEye } from 'react-icons/fa';
import './MyListings.css';
import ReceiptModal from './ReceiptModal';
import { useNavigate } from 'react-router-dom';

const MyListings = () => {
  const [listings, setListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (!user) return;

      const houseQuery = query(
        collection(db, 'house-listings'),
        where('ownerId', '==', user.uid)
      );

      const unsubHouse = onSnapshot(houseQuery, async (houseSnapshot) => {
        const houseListings = await Promise.all(
          houseSnapshot.docs.map(async (docSnap) => {
            const data = docSnap.data();
            let thumbnailUrl = '';
            if (data.thumbnailPath) {
              const thumbnailRef = ref(storage, data.thumbnailPath);
              thumbnailUrl = await getDownloadURL(thumbnailRef);
            }
            return {
              id: docSnap.id,
              ...data,
              thumbnailUrl,
              collectionName: 'house-listings',
              isVacant: data.isVacant !== undefined ? data.isVacant : true,
              showBooked: data.showBooked !== undefined ? data.showBooked : false,
              views: typeof data.views === 'number' ? data.views : 0
            };
          })
        );

        setListings((prev) => {
          const rentOnly = prev.filter((l) => l.collectionName === 'rent-listings');
          return [...houseListings, ...rentOnly];
        });
      });

      const rentQuery = query(
        collection(db, 'rent-listings'),
        where('ownerId', '==', user.uid)
      );

      const unsubRent = onSnapshot(rentQuery, async (rentSnapshot) => {
        const rentListings = await Promise.all(
          rentSnapshot.docs.map(async (docSnap) => {
            const data = docSnap.data();
            let thumbnailUrl = '';
            if (data.thumbnailPath) {
              const thumbnailRef = ref(storage, data.thumbnailPath);
              thumbnailUrl = await getDownloadURL(thumbnailRef);
            }
            return {
              id: docSnap.id,
              ...data,
              thumbnailUrl,
              collectionName: 'rent-listings',
              isVacant: data.isVacant !== undefined ? data.isVacant : true,
              showBooked: data.showBooked !== undefined ? data.showBooked : false,
              views: typeof data.views === 'number' ? data.views : 0
            };
          })
        );

        setListings((prev) => {
          const houseOnly = prev.filter((l) => l.collectionName === 'house-listings');
          return [...houseOnly, ...rentListings];
        });
      });

      return () => {
        unsubHouse();
        unsubRent();
      };
    });

    return () => unsubscribeAuth();
  }, []);

  const handleDelete = async (listing) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this listing?");
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, listing.collectionName, listing.id));
      if (listing.thumbnailPath) await deleteObject(ref(storage, listing.thumbnailPath));
      if (listing.slotImagePaths?.length > 0) {
        for (const path of listing.slotImagePaths) path && await deleteObject(ref(storage, path));
      }
      if (listing.videoPaths?.length > 0) {
        for (const path of listing.videoPaths) path && await deleteObject(ref(storage, path));
      }
      setListings((prev) => prev.filter((l) => l.id !== listing.id));
    } catch (error) {
      console.error('Failed to delete listing and media:', error);
      alert('Failed to delete listing. Please try again.');
    }  
  };

  const toggleVacancy = async (listing) => {
    try {
      const listingRef = doc(db, listing.collectionName, listing.id);
      const newVacancy = !listing.isVacant;
      await updateDoc(listingRef, { isVacant: newVacancy, showBooked: !newVacancy });
      setListings((prev) =>
        prev.map((l) => l.id === listing.id ? { ...l, isVacant: newVacancy, showBooked: !newVacancy } : l)
      );
    } catch (err) {
      console.error("❌ Failed to toggle vacancy:", err);
    }
  };

  return (
    <div className="user-listings-wrapper">
      <button className="backy" onClick={() => navigate("/homepage/profile")}>
        <span className="material-icons">arrow_back</span> Back
      </button>

      <div className="listing-info-section">
        <h2><FaShieldAlt className="info-icon" /> Lifetime Listing for Your Property</h2>
        <p>Once your property is listed, it will remain live forever. If you want to make any changes or updates to your listing, you must purchase a new plan.</p>
        <p className="fraud-note">
          <FaExclamationTriangle className="note-icon" /> <strong>Important:</strong> If any verification details or listing details are found to be fraudulent or fake, the listing will be immediately deleted.
        </p>
      </div>

      <h2 className="section-title">My Property Listings</h2>

      {listings.length === 0 ? (
        <p className="no-listings-msg">No listings found.</p>
      ) : (
        <div className="user-listings-grid">
          {listings.map((listing) => (
           <div className="user-listing-card" key={listing.id}>

  {/* Booked small tag */}
  {!listing.isVacant && <div className="bookeds-overlay">Booked</div>}

  {/* Thumbnail */}
  {listing.thumbnailUrl && (
    <img src={listing.thumbnailUrl} alt={listing.pgName} className="user-listing-thumbnail" />
  )}

  {/* Listing details */}
  {/* Listing details */}
      <div className="user-listing-details">
        <h3>{listing.pgName}</h3>
        <p><strong>Rent:</strong> ₹{listing.rent} /month</p>
        <p><strong>Address:</strong> {listing.address}</p>
        <p><strong>Occupancy:</strong> {listing.occupancy}</p>
        <p><strong>Type:</strong> {listing.roomType || 'N/A'}</p>
        <p><strong>Contact:</strong> {listing.whatsapp}</p>
        <p className="views"><FaEye /> {listing.views} views</p>
        {listing.mapLink && (
          <a href={listing.mapLink} target="_blank" rel="noopener noreferrer">
            View on Map
          </a>
        )}
      </div>
  {/* Buttons overlay: ALWAYS clickable */}
  <div className="listing-buttons-overlay">
    <button className="vacancy-button" onClick={() => toggleVacancy(listing)}>
      {listing.isVacant ? 'Mark as Booked' : 'Mark as Vacant'}
    </button>
    <button className="delete-button" onClick={() => handleDelete(listing)}>Delete</button>
    <button className="receipt-button" onClick={() => setSelectedListing(listing)}>Generate Receipt</button>
  </div>
</div>

          ))}
        </div>
      )}

      {selectedListing && <ReceiptModal listing={selectedListing} onClose={() => setSelectedListing(null)} />}
    </div>
  );
};

export default MyListings;
